import { z } from "zod";

/**
 * Statusbericht je (Beauftragung × Monat).
 *
 * Ablösung der App `ServiceStatusberichte`. Fachliche Herkunft der Regeln:
 * deren `core.py` / `data.py` — dort aus einer SharePoint-Excel gelesen, hier
 * aus den Salesforce-Spiegeltabellen des CRM.
 */

/** Berichtsmonat als `YYYY-MM` — im Bericht zählt immer der ganze Monat. */
export const reportMonthSchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Erwartet wird YYYY-MM.");

/**
 * Bezeichnungen, die ein Projekt aus der automatischen Budget-Auswahl
 * ausschließen. Wörtlich übernommen aus `data.py::budget_for` — Pauschalen sind
 * kein abrufbares Kontingent, PEP und SuccessFactors sind eigene Produkte und
 * gehören nicht in den HCM-Servicebericht.
 */
export const BUDGET_EXCLUDED_TERMS = ["pauschal", "successfactor"] as const;

/** Eigenes Muster, weil „pep" als Teilwort in unverfänglichen Namen vorkommt. */
export const BUDGET_EXCLUDED_WORD_PATTERN = /\bpep\b/;

/**
 * Bezeichnungen, die ein Projekt als Servicekontingent ausweisen. Greift nur,
 * wenn die Abrechnungsart stimmt (s. `selectReportBudgets`).
 */
export const BUDGET_INCLUDED_TERMS = ["service", "kontingent"] as const;

/** Wie viele Budgets die Automatik höchstens in den Bericht nimmt. */
export const BUDGET_AUTO_LIMIT = 2;

/**
 * Bezeichnungen, die aus dem Auswahl-Pool fallen. Nur Pauschalen — anders als
 * bei der Automatik bleiben PEP und SuccessFactors wählbar, falls jemand sie
 * bewusst in einen Bericht nehmen will (`data.py::budgets_available`).
 */
export const BUDGET_POOL_EXCLUDED_TERMS = ["pauschal"] as const;

/**
 * Warum die Budget-Automatik ein Budget NICHT in den Bericht nimmt (Befund U1).
 *
 * Der Erklärtext an der Auswahl nannte nur „die zwei mit dem größten Rest" und
 * passte damit nicht zum Ergebnis: Bei August Storck stand genau ein Budget im
 * Bericht, ausgerechnet das mit dem **kleinsten** Rest, während eines mit
 * 8.388 € Rest und Status LÄUFT fehlte. Die Regel ist mehrstufig (s.
 * `selectReportBudgets`), und welche Stufe ein Budget reißt, war von außen
 * nicht ablesbar.
 *
 * Deshalb ein **Code** statt eines Satzes — aufgelöst wird er in der
 * Oberflächensprache des Bearbeiters (`pages.statusReports.autoReason.<code>`).
 * Der Hinweis richtet sich an den Bearbeiter, nie an den Kunden.
 */
export const budgetAutoReasonSchema = z.enum([
  /** Status ist nicht „läuft". */
  "not-running",
  /** Abrechnungsart ist nicht „Rechnung". */
  "billing-not-invoice",
  /** Pauschale, PEP oder SuccessFactors — eigenes Produkt, kein Kontingent. */
  "excluded-product",
  /** Bezeichnung weist kein Servicekontingent aus. */
  "not-service-budget",
  /** Verlängerung, und es gibt ein laufendes Kontingent daneben. */
  "renewal-superseded",
  /** Erfüllt alles, fällt aber aus der Obergrenze von zwei Budgets. */
  "rank-cut",
  /**
   * Projektumsetzungs-Projekt. Die Automatik waehlt es NIE — ein
   * Umsetzungsprojekt ist kein Servicekontingent, und ob es in den Bericht
   * gehoert, weiss nur der Mensch, der ihn schreibt.
   */
  "pu-project",
]);
export type BudgetAutoReason = z.infer<typeof budgetAutoReasonSchema>;

/**
 * Sortierreihenfolge der offenen Tickets — 1:1 aus `core.py::STATUS_RANK` der
 * abgeloesten App. Kleiner = weiter oben; Ausgesetzt/Abgebrochen ans Ende.
 *
 * Bewusst eine geordnete Liste statt eines Objekts: Wie im Original gewinnt der
 * ERSTE Eintrag, dessen Text im Status vorkommt — bei einem Objekt haenge das
 * an der Schluessel-Reihenfolge.
 */
export const TICKET_STATUS_RANK: ReadonlyArray<readonly [string, number]> = [
  ["in arbeit", 0],
  ["in progress", 0],
  ["to test", 1],
  ["to transport", 2],
  ["klassifiziert", 3],
  ["wartet auf weiteres", 5],
  ["wartet", 5],
  ["warten", 4],
  ["warte auf", 4],
  ["waiting", 4],
  ["to production", 6],
  ["ausgesetzt", 9],
  ["abgebrochen", 9],
  ["cancelled", 9],
];

/** Rang eines Ticket-Status; unbekannte Status landen hinten. */
export function rankTicketStatus(status: string | null | undefined): number {
  const s = (status ?? "").toLowerCase();
  for (const [needle, rank] of TICKET_STATUS_RANK) {
    if (s.includes(needle)) return rank;
  }
  return 99;
}

/**
 * Offene Tickets nach Status sortieren (`core.py::sort_tickets`). Stabil —
 * gleicher Rang behaelt die vorhandene Reihenfolge.
 */
export function sortTicketsByStatus<T extends { ticketStatus: string | null }>(
  tickets: T[],
): T[] {
  return tickets
    .map((ticket, index) => ({ ticket, index }))
    .sort(
      (a, b) =>
        rankTicketStatus(a.ticket.ticketStatus) -
          rankTicketStatus(b.ticket.ticketStatus) || a.index - b.index,
    )
    .map((entry) => entry.ticket);
}

export const statusReportBudgetSchema = z.object({
  projectId: z.string().uuid(),
  name: z.string(),
  /**
   * Woher das Budget kommt. `pu` = Bereich Projektumsetzung, gespiegelt in
   * `pu_projects` und nur auf ausdrueckliche Auswahl im Bericht. Die
   * Oberflaeche kennzeichnet solche Zeilen, damit niemand ein
   * Umsetzungsprojekt fuer ein Servicekontingent haelt.
   */
  source: z.enum(["service", "pu"]),
  /** Vertragsvolumen (`Budget__c`). */
  budgetEur: z.number().nullable(),
  /** Bereits abgerufen — abgeleitet, s. `deriveBilled`. */
  billedEur: z.number().nullable(),
  /** Rest (`msBudgetLeft__c`). */
  leftEur: z.number().nullable(),
  status: z.string(),
  /**
   * Bestellnummer des Kunden, von Hand gepflegt. `null`, solange keine
   * hinterlegt ist — dann steht im Bericht auch nichts. Optional im Wortsinn.
   */
  orderNumber: z.string().nullable(),
  /** True, wenn die Automatik das Budget gewählt hat (nicht der Mensch). */
  auto: z.boolean(),
  /**
   * `null`, wenn die Automatik dieses Budget wählen würde — sonst die Stufe,
   * an der es scheitert (Befund U1). Unabhängig davon, ob gerade die Automatik
   * oder eine Handauswahl gilt: Die Angabe beschreibt die Regel, nicht den
   * aktuellen Zustand.
   */
  autoReason: budgetAutoReasonSchema.nullable(),
});
export type StatusReportBudget = z.infer<typeof statusReportBudgetSchema>;

export const statusReportAbsenceSchema = z.object({
  dateFrom: z.string(),
  dateTo: z.string(),
});
export type StatusReportAbsence = z.infer<typeof statusReportAbsenceSchema>;

export const statusReportContactSchema = z.object({
  employeeId: z.string().uuid(),
  fullName: z.string(),
  initials: z.string(),
  email: z.string(),
  role: z.enum(["lead", "deputy"]),
  /**
   * Kommende Abwesenheiten (90-Tage-Fenster wie in der Altanwendung).
   * Enthält bewusst KEINEN Abwesenheitsgrund — Vorgabe 2026-08-02.
   */
  absences: z.array(statusReportAbsenceSchema),
});
export type StatusReportContact = z.infer<typeof statusReportContactSchema>;

export const statusReportTicketSchema = z.object({
  /** Nur bei `source: "manual"` gesetzt — Handhabe zum Entfernen der Zeile. */
  overrideId: z.string().uuid().nullable(),
  jiraKey: z.string().nullable(),
  summary: z.string(),
  ticketStatus: z.string().nullable(),
  /** Hebt ein erledigtes Ticket in den „Highlight des Monats"-Kasten. */
  highlight: z.boolean(),
  /** Nutzenbeschreibung unter dem Highlight. */
  valueDescription: z.string().nullable(),
  /** KI-Zusammenfassung, kursiv unter dem Ticket-Titel. */
  aiSummary: z.string().nullable(),
  /**
   * Lösungsdatum als `YYYY-MM-DD` — nur bei erledigten Zeilen aus dem Spiegel.
   *
   * Kommt aus dem Spiegel, nicht aus der Handpflege: Bei einem Kunden mit
   * sechzig erledigten Vorgängen im Monat (DENIOS SE, Mai–Juli 2026: 47/57/60)
   * ist das Datum der Anker, an dem sich die Liste überhaupt noch lesen lässt.
   */
  resolvedOn: z.string().nullable(),
  /**
   * „Status und nächste Schritte" — Freitext, den der Service Lead pflegt.
   *
   * Optional im Wortsinn: Ist das Feld bei KEINEM Vorgang eines Abschnitts
   * gefüllt, erscheint die Spalte im Bericht gar nicht. Eine leere Spalte
   * beim Kunden wäre schlechter als keine.
   *
   * Hängt an der Vorgangsnummer, nicht an der Zeile — Spiegel-Vorgänge sollen
   * eine Notiz tragen können, ohne durch eine Handzeile ersetzt zu werden.
   * Handzeilen ohne Nummer tragen deshalb keine.
   */
  nextSteps: z.string().nullable(),
  /**
   * Im Kundenbericht ausgeblendet. In der Bearbeitungsansicht bleibt die Zeile
   * sichtbar, damit man sie wieder einblenden kann — ausgeblendet heisst
   * nicht geloescht.
   */
  hidden: z.boolean(),
  /** `manual` = per Override gepflegt; `mirror` = aus dem Ticket-Spiegel. */
  source: z.enum(["manual", "mirror"]),
});

/**
 * Eingabe für „Status und nächste Schritte".
 *
 * Leerer Text bedeutet „Notiz entfernen" — deshalb kein `min(1)`. Die
 * Obergrenze ist großzügig, aber vorhanden: Der Text geht an den Kunden und
 * steht in einer Tabellenspalte, nicht in einem Aufsatz.
 */
export const statusReportTicketNoteInputSchema = z.object({
  jiraKey: z.string().min(1).max(64),
  nextSteps: z.string().max(2000),
});
export type StatusReportTicketNoteInput = z.infer<
  typeof statusReportTicketNoteInputSchema
>;
export type StatusReportTicket = z.infer<typeof statusReportTicketSchema>;

/**
 * Eine Zeile der Ticketstatistik: ein Monat, vier Vorgangsarten, „offen".
 *
 * Die Spalten sind die der abgeloesten Anwendung, und sie bleiben es: Der
 * Bericht geht an Kunden, die diese Tabelle seit Jahren bekommen.
 */
export const statusReportTicketStatsRowSchema = z.object({
  /** `2025-01` — maschinenlesbar, fuer Sortierung und Tests. */
  month: z.string(),
  /** `JAN 25` — so steht es beim Kunden. */
  label: z.string(),
  incident: z.number().int(),
  problem: z.number().int(),
  change: z.number().int(),
  serviceRequest: z.number().int(),
  /**
   * Von den in diesem Monat angelegten Vorgaengen stehen so viele **heute**
   * auf „In Arbeit" — nicht der Stand am Monatsende. Uebernommen aus der
   * Altanwendung; die Zahl aendert sich rueckwirkend.
   */
  open: z.number().int(),
});
export type StatusReportTicketStatsRow = z.infer<
  typeof statusReportTicketStatsRowSchema
>;

/**
 * Einen Vorgang im Kundenbericht ausblenden oder wieder einblenden.
 *
 * Gilt fuer die **ganze Beauftragung**, nicht fuer einen Monat: Der Anlass ist
 * das Steering-Ticket, und wer das ausblendet, meint nicht „diesen Monat
 * nicht".
 */
export const statusReportHideTicketInputSchema = z.object({
  jiraKey: z.string().min(1).max(64),
  hidden: z.boolean(),
});
export type StatusReportHideTicketInput = z.infer<
  typeof statusReportHideTicketInputSchema
>;

/**
 * Bestellnummer eines Budgets setzen oder entfernen.
 *
 * Leerer Text entfernt sie — sonst bliebe eine leere Zeile stehen und der
 * Bericht zeigte ein Feld ohne Inhalt.
 */
export const statusReportOrderNumberInputSchema = z.object({
  projectId: z.string().uuid(),
  orderNumber: z.string().max(80),
});
export type StatusReportOrderNumberInput = z.infer<
  typeof statusReportOrderNumberInputSchema
>;

export const statusReportHighlightSchema = z.object({
  id: z.string().uuid(),
  body: z.string(),
  sortOrder: z.number().int(),
});
export type StatusReportHighlight = z.infer<typeof statusReportHighlightSchema>;

/** Termin aus dem Veranstaltungskalender (activate-hr.de). */
export const statusReportEventSchema = z.object({
  date: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  eventType: z.string().nullable(),
  url: z.string().nullable(),
});
export type StatusReportEvent = z.infer<typeof statusReportEventSchema>;

/**
 * Offen benannte Lücken im Bericht — als **Code**, nicht als Satz.
 *
 * Zuvor schickte das Backend fertige deutsche Sätze, die die Oberfläche
 * unverändert anzeigte; in der englischen Oberfläche standen dann deutsche
 * Meldungen (Befund W8). Hinweise richten sich an den Bearbeiter, nicht an den
 * Kunden — sie folgen deshalb der Oberflächensprache und werden im Frontend
 * über `pages.statusReports.notice.<code>` aufgelöst.
 */
export const statusReportNoticeSchema = z.enum([
  /** Keine Salesforce-Projekte an der Beauftragung — Budget und Stunden leer. */
  "no-salesforce-project",
  /** Projekte vorhanden, aber keines erfüllt die Budget-Automatik. */
  "no-auto-budget",
  /** Ticketzeilen stammen ausschließlich aus der Handpflege. */
  "no-ticket-mirror",
]);
export type StatusReportNotice = z.infer<typeof statusReportNoticeSchema>;

export const statusReportConfigSchema = z.object({
  selectedProjectIds: z.array(z.string().uuid()).nullable(),
  recipients: z.array(z.string().email()),
  ccRecipients: z.array(z.string().email()),
  salutation: z.string().nullable(),
  /** „Sie (förmlich)" oder „Du (persönlich)" — steuert Anrede und Einleitung. */
  salutationMode: z.enum(["sie", "du"]),
  /** Vorname, nur bei „Du" verwendet. */
  salutationName: z.string().nullable(),
  showTicketStats: z.boolean(),
});
export type StatusReportConfig = z.infer<typeof statusReportConfigSchema>;

export const statusReportResponseSchema = z.object({
  reportId: z.string().uuid().nullable(),
  engagementId: z.string().uuid(),
  engagementName: z.string(),
  customerId: z.string().uuid(),
  customerName: z.string(),
  segmentId: z.string().uuid(),
  segmentName: z.string(),
  reportMonth: reportMonthSchema,
  status: z.enum(["draft", "sent"]),
  budgets: z.array(statusReportBudgetSchema),
  /**
   * Alle Budgets der Beauftragung zur Auswahl — laufende zuerst, darin nach
   * Restbudget. Entspricht `data.py::budgets_available`: bewusst breiter als
   * die Automatik, damit auch ein geplantes oder abgeschlossenes Kontingent
   * in den Bericht genommen werden kann.
   */
  availableBudgets: z.array(statusReportBudgetSchema),
  /**
   * Stichtag der Budgetzahlen als `YYYY-MM-DD` — `null`, wenn kein Budget im
   * Bericht steht (Befund B3).
   *
   * Budget, Rest und Verbrauch stehen in Salesforce als **einzelner
   * Jetzt-Wert** am `Projekt__c` (`Budget__c`, `msBudgetLeft__c`,
   * `Totally_billed_netto__c`) und werden vom Sync destruktiv überschrieben.
   * Ein Stand zum Ende eines vergangenen Berichtsmonats lässt sich daraus
   * nicht ableiten — auch nicht aus `assignment_monthly_revenues`, der
   * einzigen monatlich aufgelösten Spiegel-Tabelle: Sie trägt Umsatz und
   * Stunden je Kontierungsmonat, aber weder Budget noch Abruf (TK §12.5).
   *
   * Statt eine Historie zu erfinden, nennt der Bericht den Stichtag. Er kommt
   * aus dem `synced_at` der zugrunde liegenden Spiegelzeilen, nicht aus dem
   * heutigen Datum: Steht der Sync, sagt der Bericht das, statt Aktualität zu
   * behaupten.
   */
  budgetAsOf: z.string().nullable(),
  config: statusReportConfigSchema,
  /** Faktura-Stunden des Berichtsmonats, auf Viertelstunden gerundet. */
  hours: z.number().nullable(),
  contacts: z.array(statusReportContactSchema),
  /**
   * Ticketstatistik ueber 24 Monate — **nur** wenn der Haken gesetzt ist, sonst
   * `null`. Der Unterschied ist wichtig: Eine leere Liste hiesse „keine
   * Vorgaenge", `null` heisst „nicht angefordert", und die teure Abfrage
   * unterbleibt dann auch.
   */
  ticketStats: z.array(statusReportTicketStatsRowSchema).nullable(),
  /**
   * Wann der naechtliche Lauf die Vorgaenge zuletzt gesehen hat — `null`, wenn
   * keine Kundenabteilung verknuepft ist. Steht nur in der
   * Bearbeitungsansicht, nicht im Kundenbericht.
   */
  ticketsSyncedAt: z.string().nullable(),
  openTickets: z.array(statusReportTicketSchema),
  doneTickets: z.array(statusReportTicketSchema),
  highlights: z.array(statusReportHighlightSchema),
  /** Nächste Veranstaltungen — im Bericht als Abschnitt „Events & Webinare". */
  events: z.array(statusReportEventSchema),
  /** Frei bearbeitete Fassung; gesetzt = sie gewinnt über die generierte. */
  editedHtml: z.string().nullable(),
  /** Basis für Ticket-Links, z. B. „https://…/servicedesk/customer/portal/1". */
  jiraTicketBaseUrl: z.string().nullable(),
  /**
   * Offen benannte Lücken statt stiller Leerstellen — als Code, aufgelöst in
   * der Oberflächensprache des Bearbeiters (s. `statusReportNoticeSchema`).
   */
  notices: z.array(statusReportNoticeSchema),
});
export type StatusReportResponse = z.infer<typeof statusReportResponseSchema>;

/**
 * Der Bericht als Mail (`GET /status-reports/:engagementId/mail`) — dasselbe,
 * was „Bericht kopieren" in die Zwischenablage legt, dazu Betreff und
 * Empfänger des Entwurfs. Versendet wird nichts; das bleibt im Mailprogramm.
 */
export const statusReportMailSchema = z.object({
  betreff: z.string(),
  an: z.array(z.string()),
  cc: z.array(z.string()),
  /** Berichts-HTML für Outlook (Farben zusätzlich als `<font color>`). */
  html: z.string(),
  /** Klartextfassung des Berichts (`text/plain` der Zwischenablage). */
  text: z.string(),
});
export type StatusReportMail = z.infer<typeof statusReportMailSchema>;

/**
 * Eintrag der Auswahlliste — Beauftragungen, für die die angemeldete Person
 * einen Bericht sehen darf (`GET /status-reports/engagements`).
 *
 * Steht hier und nicht je einmal in Front- und Backend: Beide Seiten hatten
 * dieselbe Form von Hand getippt (Befund G9), was die Projektregel „Shapes als
 * Zod-Schema in `packages/shared`" verletzt und stillschweigend auseinander
 * laufen kann.
 */
export const reportableEngagementSchema = z.object({
  engagementId: z.string().uuid(),
  engagementName: z.string(),
  customerName: z.string(),
  segmentId: z.string().uuid(),
  segmentName: z.string(),
  /** Service Lead — Gruppierungsschlüssel der Auswahlliste (wie im Original). */
  leadName: z.string().nullable(),
  /**
   * Gab es im Vormonat gebuchte Stunden? Die Altanwendung zeigte standardmäßig
   * nur solche Kunden; „alle anzeigen" hebt den Filter auf.
   */
  activeLastMonth: z.boolean(),
  /** True, wenn die eigene Person Lead oder Stellvertreter ist. */
  own: z.boolean(),
  /** Persoenlicher Stern am Kunden — zweites Sortierkriterium nach `own`. */
  favorit: z.boolean(),
});
export type ReportableEngagement = z.infer<typeof reportableEngagementSchema>;

/** Ergebnis eines KI-Zusammenfassungslaufs (`POST …/ai-summaries`). */
export const statusReportAiSummaryResultSchema = z.object({
  generated: z.number().int(),
  skipped: z.number().int(),
  /** Fehlermeldungen der Zeilen, die ohne Zusammenfassung bleiben. */
  failures: z.array(z.string()),
});
export type StatusReportAiSummaryResult = z.infer<
  typeof statusReportAiSummaryResultSchema
>;

/**
 * Antwort der beiden Versand-Schalter (`POST …/send`, `DELETE …/send`).
 *
 * Beide Richtungen liefern dieselbe Form — der Rückweg ist kein Sonderfall,
 * sondern die Umkehrung (Befund B8). Vorher gab es die Form nur als
 * handgetippten Rückgabetyp im Dienst; jetzt steht sie einmal hier und wird in
 * Front- und Backend verwendet.
 */
export const statusReportSendResultSchema = z.object({
  reportId: z.string().uuid(),
  status: z.enum(["draft", "sent"]),
});
export type StatusReportSendResult = z.infer<
  typeof statusReportSendResultSchema
>;

export const statusReportConfigInputSchema = statusReportConfigSchema.partial();
export type StatusReportConfigInput = z.infer<
  typeof statusReportConfigInputSchema
>;

/** Body von POST /status-reports/:engagementId/edited — der von Hand bearbeitete Berichts-HTML. */
export const statusReportEditedInputSchema = z.object({
  html: z.string().min(1).max(500_000),
});
export type StatusReportEditedInput = z.infer<typeof statusReportEditedInputSchema>;

export const statusReportHighlightInputSchema = z.object({
  body: z.string().min(1).max(2000),
  sortOrder: z.number().int().min(0).optional(),
});
export type StatusReportHighlightInput = z.infer<
  typeof statusReportHighlightInputSchema
>;

export const statusReportOverrideInputSchema = z.object({
  section: z.enum(["open", "done"]),
  jiraKey: z.string().max(40).nullable().optional(),
  summary: z.string().min(1).max(500),
  ticketStatus: z.string().max(80).nullable().optional(),
  highlight: z.boolean().optional(),
  valueDescription: z.string().max(500).nullable().optional(),
  sortOrder: z.number().int().min(0).optional(),
});
export type StatusReportOverrideInput = z.infer<
  typeof statusReportOverrideInputSchema
>;
